/**
 * SplitEasy in-app knowledge base.
 *
 * Edit this file to add/change help articles. No DB, no API — pure static
 * content shipped with the bundle. Each article supports:
 *   id          stable slug for deep-linking
 *   title       sidebar label
 *   icon        lucide-react component
 *   category    grouping in the sidebar
 *   sections    array of { heading, body (string with line breaks) }
 *   related     array of other article ids to show in "Related" footer
 */
import {
  Wallet, Receipt, Calendar, Users, AlertCircle, Sparkles,
  ScrollText, FileCheck, Trash2, RefreshCw, Bell, ShieldCheck,
  Map,
} from 'lucide-react';
import NXCIcon from './components/NXCIcon';

export const HELP_CATEGORIES = [
  { id: 'getting-started', label: 'Getting Started' },
  { id: 'money', label: 'Money & money' },
  { id: 'sessions', label: 'Sessions & Attendance' },
  { id: 'admin', label: 'Admin Tools' },
  { id: 'troubleshooting', label: 'Troubleshooting' },
];

export const HELP_ARTICLES = [
  // ============== Getting Started ==============
  {
    id: 'whats-new-may-2026',
    title: 'Where did everything move? (May 2026 redesign)',
    icon: Map,
    category: 'getting-started',
    sections: [
      {
        heading: 'The group page now has 3 tabs instead of 7',
        body: `If you've used SplitEasy before, here's the cheat-sheet for what moved where:

**Old tab → New home**
- Expenses → **Ledger → Expenses** filter
- Recurring → **Ledger → Recurring** filter
- Fund (transactions) → **Ledger → Fund** filter
- Fund (summary cards) → **Overview** tab
- Attend / Sessions → **People → Sessions** filter
- Members → **People → Members** filter
- Settle / Balances → **People → Balances** filter
- AI assistant → **✨ sparkle** icon in the group header

**Old buttons → New homes**
- Top up · Allot Money · Transfer · all 4 fines · Loan to Member · Quick Settle · Imports · Fine defaults · Reset → all behind the **+** Floating Action Button (bottom-right, admin only)
- Settings · Analytics · Reconcile · Audit log · Trash · Delete group → behind the **⋮** menu in the group header

That's it — fewer surfaces, less hunting, same features.`,
      },
    ],
    related: ['tabs-overview'],
  },
  {
    id: 'fund-vs-coins',
    title: 'Group Fund vs Member money',
    icon: Wallet,
    category: 'getting-started',
    sections: [
      {
        heading: 'Two separate ledgers',
        body: `SplitEasy decouples shared money from personal balances.

**Group Fund** is the shared treasury — what the group owns collectively. Members top it up; it pays for group expenses (court charges, refreshments, equipment).

**Member money (₹)** are each person's individual ledger. money are credits/debits attributable to a single member. Fines deduct money. money allotments add money. Peer transfers move money between members.

The two are independent — adjusting one does not auto-adjust the other.`,
      },
      {
        heading: 'When does each move?',
        body: `**Fund changes when:** member tops up (Cr), group-fund expense paid (Dr), session fee charged (Cr), reimbursement to a member (Dr).

**money change when:** admin issues a fine (member −, fund +), admin allots money (member +, fund unchanged), member transfers money to another member (sender −, recipient +, fund unchanged).`,
      },
    ],
    related: ['add-expense', 'fines', 'reconcile'],
  },
  {
    id: 'tabs-overview',
    title: 'What each tab shows',
    icon: ScrollText,
    category: 'getting-started',
    sections: [
      {
        heading: 'The 3 group tabs',
        body: `Every group has just three tabs at the top, plus a few smart shortcuts.

**Overview** — at-a-glance health: open loans, cash in hand vs payment-pending, monthly momentum, weekly fines, top offenders, pending money transfers. The Fund Hero (Group Fund balance) sits above all tabs and is always visible.

**Ledger** — your unified financial history. Three filter chips switch the feed:
- **Expenses** (default): peer-paid + group-fund-paid expenses
- **Fund**: the treasury transaction ledger with running balance, Excel/PDF export
- **Recurring**: auto-firing expenses on a schedule

**People** — everyone in this group. Three filter chips:
- **Members** (default): roster, balances, per-member ledger (📖 icon), promote/demote, remove
- **Balances**: peer settlement suggestions (Splitwise-style "Amit pays Kalpesh 500")
- **Sessions**: RSVP + attendance + auto-fines`,
      },
      {
        heading: 'Smart shortcuts in the header',
        body: `Look at the top of any group page:

**✨ sparkle** — opens the AI assistant (Co-Admin chat). Ask "Who owes the most?" or "Why is Pickle Group's drift 200?".

**⋮ menu** — Analytics · Reconcile · Audit log · Trash · Delete group. Anything you don't use every day lives here.

**+ FAB** (bottom-right, admin only) — Top Up · Allot Money · Transfer · Settle · 4 fine types · Guest Contribution · Loan to Member · Imports · Configure · Reset.`,
      },
    ],
    related: ['fund-vs-coins', 'reconcile'],
  },
  {
    id: 'add-expense',
    title: 'Recording an expense',
    icon: Receipt,
    category: 'getting-started',
    sections: [
      {
        heading: 'Two payment paths',
        body: `**Paid by a member**: select who paid + which members share the cost. The payer's balance goes up by the others' shares; debtors' balances go down.

**Paid from Group Fund**: select "Group Fund" as payer. Fund balance drops by the full amount; no coin movement; no settlement obligation.

For peer-paid expenses, you can also choose:
- Equal split (default)
- Custom split (enter exact amount per person)
- Exclude yourself (you paid, don't owe yourself)

Tap **Ledger → Expenses → Add Expense** to start.`,
      },
      {
        heading: 'Approval workflow',
        body: `If your group has approval enabled, expenses go to **Pending** until an admin approves. Members get notified. AI Co-Admin can auto-approve low-stakes items if enabled.

To reverse: **Ledger → Expenses** → tap the ↻ icon on the row. money refund + fund restores. 5-second undo toast also appears.`,
      },
    ],
    related: ['fund-vs-coins', 'fines'],
  },

  // ============== Money & Coins ==============
  {
    id: 'top-up-fund',
    title: 'How to top up the fund',
    icon: Wallet,
    category: 'money',
    sections: [
      {
        heading: 'Adding money to the shared treasury',
        body: `Tap the **+** Floating Action Button at the bottom-right (admin only) → **Top Up** → enter member + amount.

The member is being credited the cash they handed over (in real life). The fund balance increases by that amount. A "contribution" row appears in **Ledger → Fund** with a "Top Up" badge.

Top-ups can be reversed if entered by mistake — admins see a ↻ icon on contribution rows in **Ledger → Fund**.`,
      },
    ],
    related: ['fund-vs-coins'],
  },
  {
    id: 'fines',
    title: 'Fines: types and when they apply',
    icon: AlertCircle,
    category: 'money',
    sections: [
      {
        heading: 'Four fine types',
        body: `**Late ≤Xm** — small late penalty (default 100). Applied via Mark Attendance or manually.

**Late >Xm** — bigger late penalty (default 200). Applied via Mark Attendance or manually.

**No Show** — full penalty for not showing up after RSVP-confirmed (default 500). Auto-triggered if attendance not marked within 24h.

**Late Cancel** — penalty for cancelling RSVP after the cutoff (default 300). Auto-triggered when member cancels too late.

**Cancel** — administrative penalty for cancelling sessions (default 300). Manually applied.

Amounts and thresholds are configurable per group from the **+** FAB → **Fine defaults** (admin only).`,
      },
      {
        heading: 'Auto-fines from Mark Attendance',
        body: `Go to **People → Sessions** → on a session ready to confirm, tap **Mark Attendance**. Each attendee gets four buttons: On time / Late ≤Xm / Late >Xm / Absent.

Fines are issued atomically when you save — money deduct, fund increases, audit log updated, member gets a push notification.

If a member is at their balance limit, the fine is skipped (count shown in success toast).`,
      },
    ],
    related: ['mark-attendance', 'reverse-fine'],
  },
  {
    id: 'reverse-fine',
    title: 'Reversing a fine',
    icon: RefreshCw,
    category: 'money',
    sections: [
      {
        heading: 'How to undo',
        body: `Go to **Ledger → Fund** → find the fine row → tap the ↻ icon (admin only).

money are credited back to the member, fund balance decreases, an audit log entry is written. The original fine row gets struck through (preserved for history).

Fine reversals cannot themselves be reversed — to undo a reversal, manually issue a new fine of the same amount.`,
      },
    ],
    related: ['fines'],
  },
  {
    id: 'money-transfer',
    title: 'Sending money to another member',
    icon: NXCIcon,
    category: 'money',
    sections: [
      {
        heading: 'Peer-to-peer transfers',
        body: `Go to **Ledger → Expenses** → tap **Send Money** (or **Request** to ask another member to send to you).

Enter the recipient + amount + a note. Sender's money decrease, recipient's increase. Fund balance is unchanged. NeoPay audit ledger gets a mirror entry (✓ NP badge).

**Peer transfers are final by design** — they cannot be reversed unilaterally by an admin. To correct an error, the recipient must initiate a return transfer.`,
      },
    ],
    related: ['fund-vs-coins'],
  },

  // ============== Sessions ==============
  {
    id: 'mark-attendance',
    title: 'Marking attendance',
    icon: Calendar,
    category: 'sessions',
    sections: [
      {
        heading: 'One-shot 4-state save',
        body: `Go to **People → Sessions** → find the session → tap **Mark Attendance**.

For each RSVP-confirmed member, pick one of:
- **On time** (no fine)
- **Late ≤Xm 100** (auto-fine)
- **Late >Xm 200** (auto-fine)
- **Absent** (may auto-trigger no-show fine after 24h depending on group settings)

Tap **Save** — all attendance + all fines applied atomically.`,
      },
      {
        heading: 'Editing a saved session',
        body: `Tap the ⋯ menu on the session card → **Edit**. Change time, max players, courts, or close/reopen RSVP. To delete, the session goes to a 30-day Trash where it can be restored before permanent deletion.`,
      },
    ],
    related: ['fines', 'sessions-rsvp'],
  },
  {
    id: 'sessions-rsvp',
    title: 'RSVP and session lifecycle',
    icon: Users,
    category: 'sessions',
    sections: [
      {
        heading: 'Lifecycle stages',
        body: `**Open** — RSVP window is live, members can join/leave.
**Locked** — RSVP closed, no more changes from members.
**Confirmed** — admin confirmed the session is happening, late-cancel fines now apply if anyone backs out.
**Cancelled** — session was cancelled. Late-cancel fines auto-refund.

Admin can override RSVP for any member from the session card → **⋯ → Manage RSVP**.`,
      },
    ],
    related: ['mark-attendance'],
  },

  // ============== Admin tools ==============
  {
    id: 'reconcile',
    title: 'Reconciling balances',
    icon: FileCheck,
    category: 'admin',
    sections: [
      {
        heading: 'The Reconcile cockpit',
        body: `Tap the **⋮** menu in the group header → **Reconcile** (admin only). This shows everything you need to verify the books for any period.

**Health Strip**: Fund stored vs computed (drift?), Coin sum (should be 0 net), NeoPay mirror count, last-reconciled timestamp.

**Members panel**: each member with ✓ if their stored balance matches the ledger walk, ⚠ if drift detected.

**Ledger view**: classic Dr/Cr/Bal accounting format with daily subtotals + opening/closing balance + drift banner.

**Anomalies**: deterministic checks (duplicate fines, NeoPay failures, orphan late-cancel fines on cancelled sessions, balance drift). Plus opt-in **AI Deep Scan** using your OpenAI key for unusual-pattern detection.

**Tick to verify**: green checkbox on every row. localStorage + server sync, multi-admin attributed. Shift-click for range select. Tick-all / Untick-all buttons. Persists forever.

**Mark Complete**: stamps the period as reconciled + saves an immutable Snapshot capturing fund opening/Dr/Cr/closing + per-member positions + drift + anomaly count.

**Period Chat**: leave audit notes, @mention co-admins, real-time live append via SSE.`,
      },
      {
        heading: 'When stored balance ≠ computed',
        body: `If you see a drift warning, walk the ledger top-to-bottom ticking off rows you confirmed. Unticked rows are your suspects. Common causes:
- Legacy seed data set the balance directly without writing fund_transactions
- A historical migration missed an edge case
- Manual DB edits

If the drift is historical, the cleanest fix is to "Mark Complete" with a note like "Accepted X balance drift as historical opening balance" and move on.`,
      },
    ],
    related: ['fund-vs-coins', 'audit-log', 'snapshot'],
  },
  {
    id: 'snapshot',
    title: 'Reconciliation Snapshots',
    icon: FileCheck,
    category: 'admin',
    sections: [
      {
        heading: 'Immutable monthly close',
        body: `Each time you click **Mark Complete** on the Reconcile page, an immutable snapshot is saved with:
- Period label (e.g. "May 2026")
- Group fund opening / Dr / Cr / closing / drift
- Per-member opening / Dr / Cr / closing / drift
- Anomaly count at that moment
- Your note + your name

Snapshots cannot be edited or deleted. They appear in the **Reconciliation Snapshots** card on the Reconcile page. Click any to view the frozen breakdown.

Useful for: tax review, onboarding new admins, settling disputes ("On May 31 your balance was X ₹ — here's the snapshot").`,
      },
    ],
    related: ['reconcile'],
  },
  {
    id: 'audit-log',
    title: 'Audit Log',
    icon: ScrollText,
    category: 'admin',
    sections: [
      {
        heading: 'Who did what, when',
        body: `Tap the **⋮** menu in the group header → **Audit log** (admin only).

Shows last 100 actions: expense_approved, fine_issued, fine_reversal, coin_allot, member_added, session_cancelled, reconciliation_complete, late_fines_issued, etc.

Use as forensic trail when something looks off. Combine with the Reconcile cockpit's Anomaly detection for complete coverage.`,
      },
    ],
    related: ['reconcile'],
  },
  {
    id: 'ai-co-admin',
    title: 'AI Co-Admin (auto-approvals)',
    icon: Sparkles,
    category: 'admin',
    sections: [
      {
        heading: 'How auto-approvals work',
        body: `Enable in: ⋮ menu → **Group settings** → AI Co-Admin.

When enabled, AI evaluates every pending expense + every fine using:
- Member's trust tier (built up from clean history)
- Confidence score (0-100) from GPT-4o
- Group's fine defaults vs amount being applied

If trust × confidence > threshold AND amount is below auto-approve cap → AI auto-approves and writes an "ai_action" record.

You can **Veto** any AI action within 5 minutes — open the AI assistant via the **✨ sparkle** icon in the group header. Vetoed actions reverse atomically.

AI Supervisor (always on) flags anomalies even when Co-Admin is off — just as advisory notifications.`,
      },
    ],
    related: ['fines', 'reconcile'],
  },
  {
    id: 'trash-restore',
    title: 'Trash & restoring deleted items',
    icon: Trash2,
    category: 'admin',
    sections: [
      {
        heading: '30-day soft delete',
        body: `Sessions and Recurring Expenses go to **Trash** when deleted (not hard-deleted immediately). Tap the **⋮** menu in the group header → **Trash** to see deleted items.

You can **Restore** within 30 days. After 30 days, a daily cron permanently purges them. The trash drawer shows days-until-purge for each item.

Regular expenses are hard-deleted, but get a 5-second **Undo Toast** at the moment of delete — click "Undo" to bring back.`,
      },
    ],
    related: [],
  },
  {
    id: 'notifications',
    title: 'Notifications & WhatsApp',
    icon: Bell,
    category: 'admin',
    sections: [
      {
        heading: 'Channels',
        body: `Members can receive:
- **In-app notifications** (bell icon)
- **Web push** (when enabled in browser)
- **WhatsApp** (via Zavu — admin must configure API key in Profile)
- **Email** (via Zavu — same)

Set per-event preferences in Profile → Notifications.`,
      },
    ],
    related: [],
  },

  // ============== Troubleshooting ==============
  {
    id: 'all-balances-zero',
    title: 'Why all my Balances are 0',
    icon: AlertCircle,
    category: 'troubleshooting',
    sections: [
      {
        heading: 'It\'s by design — usually',
        body: `The **People → Balances** filter only shows peer-to-peer settlement obligations from expenses where one member paid personally and others owe them their share.

If your group **only uses Group Fund** (everyone tops up to the shared treasury, fund pays for everything), then no peer settlements ever exist → Balances stays at 0 forever.

That's correct behaviour. To verify the actual financial state of your group, look at:
- **Overview** tab — Group Fund hero + MTD momentum + open loans
- **Ledger → Fund** filter — running-balance ledger with Excel/PDF export
- **People → Members** → 📖 icon → per-member money ledger
- **⋮ menu → Reconcile** — forensic analysis`,
      },
    ],
    related: ['fund-vs-coins', 'reconcile'],
  },
  {
    id: 'reverse-not-showing',
    title: 'Why no Reverse button on a transaction',
    icon: AlertCircle,
    category: 'troubleshooting',
    sections: [
      {
        heading: 'Only certain types are reversible',
        body: `The Reverse (↻) button on Ledger → Fund rows only appears for **fines, money allotments, and contributions** — and only for admins.

Other types are intentionally NOT reversible from the Fund filter:
- **Expenses** — reverse from **Ledger → Expenses** instead
- **Reversals** — a reversal can't be reversed
- **Peer money transfers** — final by design (recipient must return manually)
- **Session fees** — handled via the session edit/cancel flow
- **Fund reimbursements** — different workflow

If a transaction looks wrong but isn't reversible, the cleanest fix is a **compensating entry** — issue a new transaction of the same amount in the opposite direction, with a note linking to the original.`,
      },
    ],
    related: ['reverse-fine'],
  },
  {
    id: 'fund-drift',
    title: 'Fund drift / balance mismatch',
    icon: AlertCircle,
    category: 'troubleshooting',
    sections: [
      {
        heading: 'Stored vs computed differ',
        body: `On the Reconcile page, the Fund chip may show a **drift** if the database's "fund_balance" field doesn't match the sum of all "fund_transactions".

Most common cause: legacy seed data was inserted directly into "fund_balance" without writing matching transaction rows.

Two paths to fix:
1. **Accept as historical opening** — click Mark Complete with a note explaining. The drift is preserved as the snapshot's opening balance, and going forward the running ledger is consistent.
2. **Backfill synthetic transactions** — write a one-time migration adding "contribution" rows totalling the drift. Restores stored = computed but adds artificial entries.

Path 1 is cleaner unless you need a perfectly clean ledger from day zero.`,
      },
    ],
    related: ['reconcile'],
  },
  {
    id: 'security',
    title: 'Security & data privacy',
    icon: ShieldCheck,
    category: 'troubleshooting',
    sections: [
      {
        heading: 'Where your data lives',
        body: `- App data → MongoDB hosted on Emergent. Daily backups with 7-day retention.
- NeoPay mirror → external append-only ledger for audit (only money transfers + reimbursements are mirrored).
- API keys (OpenAI, Zavu, NeoPay) → encrypted at rest in MongoDB via Fernet, fall back to env vars.
- Phone-based auth with bcrypt password hashing.

To export your data: tap the **⋮** menu in the group header → **Group settings** → Export → choose PDF or Excel.`,
      },
    ],
    related: [],
  },
  // ============== iter187 — "Why am I being fined?" auto-explainer ==============
  {
    id: 'why-am-i-fined',
    title: 'Why am I being fined? (auto-explainer)',
    icon: AlertCircle,
    category: 'sessions',
    sections: [
      {
        heading: 'The (i) icon next to every red row',
        body: `Open your ledger drawer (📖 icon in the People → Members tab on your own row, or admin can open anyone's). Every fine or session-fee row now has a small **(i)** icon next to the description.

Tap it → a popover opens with **exactly why this fine was issued**, pulled live from the audit log:

**For a late-arrival fine** — Which session, late tier (≤10min vs >10min), when it was issued, by which admin.

**For a no-show fine** — That you confirmed RSVP but were marked absent, the session, the issuing admin.

**For a late-cancel fine** — The most useful one. Shows:
- Which session you cancelled for
- The cutoff deadline (e.g. *4:00 PM on 15 May 2026* — one day before the session)
- Exactly when you cancelled (down to the minute)
- How late you were vs the cutoff (e.g. *6h 32m after cutoff*)
- Fine amount

**For a per-session fee** — Which session, how much, when charged, by whom. If the admin batched the charge (i.e. charged it on a different day than the session ran), it'll say *"Charged later by X on Y date"* so you can see that too.

**For a manual / custom fine** — Just the reason the admin typed + their name + when.`,
      },
      {
        heading: 'Why this exists',
        body: `Half the friction in shared groups comes from "wait, why was I fined?" arguments. The auto-explainer answers that without needing the admin to look it up — every fine self-documents the timestamps that triggered it.

**It does NOT change any logic** — same fines, same amounts, same admin decisions. It just shows the audit trail in human language.

**If something looks wrong** — tap the (i), screenshot the popover, and ask your admin to reverse the fine via the Fund ledger's **↻ Reverse** button (now also works on session fees as of iter186). The popover ends with a hint reminding members of this path.`,
      },
    ],
    related: ['session-fines', 'reconcile-hub'],
  },
];

export function findArticle(id) {
  return HELP_ARTICLES.find(a => a.id === id);
}

export function articlesByCategory(catId) {
  return HELP_ARTICLES.filter(a => a.category === catId);
}

export function searchArticles(query) {
  const q = query.toLowerCase().trim();
  if (!q) return HELP_ARTICLES;
  return HELP_ARTICLES.filter(a => {
    if (a.title.toLowerCase().includes(q)) return true;
    if (a.id.includes(q)) return true;
    return a.sections.some(s => s.body.toLowerCase().includes(q) || s.heading.toLowerCase().includes(q));
  });
}
